import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Star, Rocket, Crown, ShoppingCart, CreditCard, Truck, Shield, Lightning, Users, Globe, Target, ArrowRight } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const WebshopPackages = () => {
  const { t } = useTranslation();

  const packages = [
    {
      name: "Starter Kit",
      subtitle: "Voor kleine bedrijven",
      icon: <ShoppingCart className="w-8 h-8" weight="thin" />,
      price: "€79",
      period: "per maand",
      setupFee: "€149",
      description: "Voor kleine bedrijven die hun eerste webshop willen starten",
      features: [
        "Max. 50 producten",
        "Basis betaalmethoden (iDEAL, Bancontact)",
        "Basis SEO & analytics",
        "Email support",
        "Standaard webshop template (makkelijk ontwerp)",
        "Responsive design (mobiel & tablet)",
        "Basis voorraadbeheer",
        "Product categorieën",
        "SSL certificaat & hosting",
        "Basis zoekfunctie",
        "Contactformulier",
        "Social media koppelingen"
      ],
      popular: false,
      color: "from-slate-600 to-slate-700",
      bgColor: "bg-slate-50",
      borderColor: "border-slate-200"
    },
    {
      name: "Growth Booster",
      subtitle: "Voor groeiende bedrijven",
      icon: <Rocket className="w-8 h-8" weight="thin" />,
      price: "€129",
      period: "per maand",
      setupFee: "€149",
      description: "Voor groeiende bedrijven die meer producten en marketingtools nodig hebben",
      features: [
        "Alles uit Starter Kit, plus:",
        "Max. 150 producten",
        "Uitgebreide betaalmethoden (PayPal, Creditcard)",
        "Klantaccounts & wensenlijsten",
        "Marketingtools (kortingscodes, emailmarketing)",
        "Priority support (binnen 24u, ook weekend)",
        "Aangepast webshop ontwerp (meer keuze in styling)",
        "Product varianten & opties (kleur, maat, etc.)",
        "Geavanceerde zoekfunctie met filters",
        "Product reviews & ratings",
        "Uitgebreide analytics & conversie tracking",
        "Email marketing integratie",
        "Multi-language support (NL/EN)",
        "Geavanceerd voorraadbeheer",
        "Bulk product import/export",
        "Product aanbevelingen"
      ],
      popular: true,
      color: "from-emerald-600 to-emerald-700",
      bgColor: "bg-emerald-50",
      borderColor: "border-emerald-300"
    },
    {
      name: "Pro Suite",
      subtitle: "Voor ambitieuze bedrijven",
      icon: <Crown className="w-8 h-8" weight="thin" />,
      price: "€199",
      period: "per maand",
      setupFee: "€199",
      description: "Voor ambitieuze bedrijven die geen beperkingen willen",
      features: [
        "Alles uit Growth Booster, plus:",
        "Onbeperkt aantal producten",
        "Alle betaalmethoden inclusief factuur",
        "Geavanceerde automatisering & workflows",
        "Multi-store / multi-language support",
        "Geavanceerde rapportages & dashboards",
        "24/7 premium support",
        "Volledig custom webshop ontwerp (100% uniek)",
        "Custom branding & logo integratie",
        "Geavanceerde product configurator",
        "B2B functionaliteiten (wholesale pricing)",
        "Geavanceerde klantsegmentatie",
        "Custom checkout proces",
        "API integraties (CRM, boekhouding, etc.)",
        "White-label oplossingen",
        "Performance monitoring & optimalisatie",
        "Dedicated account manager",
        "Custom rapportages & dashboards",
        "Geavanceerde voorraad & order management",
        "Multi-currency support"
      ],
      popular: false,
      color: "from-indigo-600 to-indigo-700",
      bgColor: "bg-indigo-50",
      borderColor: "border-indigo-200"
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          {/* USP Badge */}
          <div className="inline-flex items-center bg-slate-100 text-slate-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Lightning className="w-4 h-4 mr-2" weight="fill" />
            Transparante prijzen - Maandelijkse abonnementen + setup
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Kies je <span className="text-slate-600">webshop pakket</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Eerlijke prijzen met een eenmalige setup fee. Van eenvoudige starter tot professionele webshop - 
            elk pakket is ontworpen om jouw online verkoop te laten groeien.
          </p>
        </motion.div>

        {/* Packages Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto"
        >
          {packages.map((pkg, index) => (
            <motion.div
              key={pkg.name}
              variants={cardVariants}
              className={`relative ${pkg.bgColor} rounded-2xl p-8 border-2 ${pkg.borderColor} hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2`}
            >
              {/* Popular Badge */}
              {pkg.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-gradient-to-r from-slate-600 to-slate-700 text-white px-6 py-2 rounded-full text-sm font-semibold shadow-lg">
                    <Star className="inline w-4 h-4 mr-2" weight="fill" />
                    Meest Gekozen
                  </div>
                </div>
              )}

              {/* Package Header */}
              <div className="text-center mb-8">
                <div className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r ${pkg.color} text-white rounded-full mb-4`}>
                  {pkg.icon}
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{pkg.name}</h3>
                <p className="text-gray-600 mb-4">{pkg.subtitle}</p>
                <div className="mb-4">
                  <span className="text-4xl font-bold text-gray-900">{pkg.price}</span>
                  <span className="text-gray-500 ml-2">{pkg.period}</span>
                  <div className="text-sm text-gray-500 mt-1">
                    + {pkg.setupFee} setup
                  </div>
                </div>
                <p className="text-gray-600 text-sm">{pkg.description}</p>
              </div>

              {/* Features List */}
              <div className="space-y-3 mb-8">
                {pkg.features.map((feature, featureIndex) => (
                  <div key={featureIndex} className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" weight="fill" />
                    <span className="text-gray-700 text-sm">{feature}</span>
                  </div>
                ))}
              </div>

              {/* CTA Button */}
              <Link to="/start-your-project" className={`inline-block w-full bg-gradient-to-r ${pkg.color} text-white py-3 px-6 rounded-lg font-semibold hover:shadow-lg transition-all duration-300 transform hover:scale-105 text-center`}>
                Start met {pkg.name}
              </Link>
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <div className="bg-gray-50 border border-gray-200 rounded-3xl p-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-500 text-white rounded-2xl mb-6">
              <Users className="w-8 h-8" weight="thin" />
            </div>
            <h3 className="text-3xl font-bold text-gray-900 mb-4">Niet zeker welk pakket bij je past?</h3>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed">
              Laat ons samen kijken naar jouw specifieke behoeften en budget. 
              We maken graag een op maat gemaakte oplossing die perfect bij je past.
            </p>
            <Link to="/contact" className="inline-flex items-center bg-blue-500 text-white px-10 py-4 rounded-2xl font-semibold hover:bg-gray-800 transition-colors">
              <span>Gratis Adviesgesprek</span>
              <ArrowRight className="w-5 h-5 ml-2" weight="thin" />
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default WebshopPackages;
